import React, { useState, useEffect, useMemo } from "react";
import { Box, Container, Typography, Grid } from "@mui/material";
import { NotificationsActive as BellIcon } from "@mui/icons-material";
import { usePredpoplachOdpovediQuery } from "../api/JsdhNotifikaceService/service/PredpoplachMutations";
import type { Predpoplach } from "../api/JsdhNotifikaceService/generated/models/Predpoplach";
import { ClockFormatted } from "./ClockFormatted";
import { AppVersion } from "./AppVersion";
import { useJednotkaQuery } from "../api/JsdhNotifikaceService/service/AdminMutations";
import JsdhNotifikaceService from "../api/JsdhNotifikaceService/service/JsdhNotifikaceService";

const amberColor = "#FF8F00";

function useCountdown(targetDate: string | undefined): string {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    if (!targetDate) return "--:--";

    const diff = new Date(targetDate).getTime() - now;
    if (diff <= 0) return "00:00";

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

interface PredpoplachDisplayProps {
    predpoplach: Predpoplach;
    deviceToken?: string;
}

const PredpoplachDisplay: React.FC<PredpoplachDisplayProps> = ({ predpoplach, deviceToken }) => {
    const { data: odpovedi } = usePredpoplachOdpovediQuery(predpoplach.id, deviceToken);
    const { data: jednotka } = useJednotkaQuery();
    const countdown = useCountdown(predpoplach.casExpirace);

    const logoUrl = jednotka?.maLogo
        ? JsdhNotifikaceService.getJednotkaLogoUrl()
        : '/favicon.svg';

    const stats = useMemo(() => {
        if (!odpovedi) return { jedu: [], nejedu: [] };
        const jedu = odpovedi.filter(o => o.odpoved === "JEDU" && !o.automaticka);
        const nejedu = odpovedi.filter(o => o.odpoved === "NEJEDU");
        return { jedu, nejedu };
    }, [odpovedi]);

    return (
        <Container maxWidth="xl">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="90vh"
                textAlign="center"
            >
                {/* Logo */}
                <img
                    src={logoUrl}
                    alt="Logo jednotky"
                    style={{ maxWidth: 200, marginBottom: 16 }}
                />

                {/* Header */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box
                        sx={{
                            bgcolor: amberColor,
                            borderRadius: '50%',
                            width: 64,
                            height: 64,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <BellIcon sx={{ fontSize: 36, color: '#fff' }} />
                    </Box>
                    <Box textAlign="left">
                        <Typography
                            variant="h6"
                            sx={{ color: amberColor, fontWeight: 700, letterSpacing: 2 }}
                        >
                            PŘEDPOPLACH
                        </Typography>
                        <Typography variant="h3" fontWeight="bold">
                            {predpoplach.nazev}
                        </Typography>
                    </Box>
                </Box>

                {/* Popis */}
                {predpoplach.popis && (
                    <Typography variant="h5" color="text.secondary" sx={{ mb: 3, maxWidth: 800 }}>
                        {predpoplach.popis}
                    </Typography>
                )}

                {/* Countdown */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        Zbývající čas
                    </Typography>
                    <Typography
                        variant="h1"
                        sx={{ color: amberColor, fontWeight: 700, fontSize: "8rem" }}
                    >
                        {countdown}
                    </Typography>
                </Box>

                {/* Počty odpovědí */}
                <Grid container spacing={4} sx={{ maxWidth: 700 }}>
                    <Grid size={{ xs: 6 }}>
                        <Box
                            sx={{
                                bgcolor: 'success.main',
                                color: '#fff',
                                borderRadius: 3,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 100,
                            }}
                        >
                            <Typography variant="h3" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                                JEDU ({stats.jedu.length})
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                        <Box
                            sx={{
                                bgcolor: 'error.main',
                                color: '#fff',
                                borderRadius: 3,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 100,
                            }}
                        >
                            <Typography variant="h3" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                                NEJEDU ({stats.nejedu.length})
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Jména odpovědí */}
                {odpovedi && odpovedi.length > 0 && (
                    <Grid container spacing={4} sx={{ maxWidth: 900, mt: 2 }}>
                        <Grid size={{ xs: 6 }}>
                            {stats.jedu.map((o) => (
                                <Typography key={o.id} variant="h6" color="success.main">
                                    {o.jmeno || `Uživatel #${o.uzivatelId}`}
                                </Typography>
                            ))}
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            {stats.nejedu.map((o) => (
                                <Typography key={o.id} variant="h6" color="error.main">
                                    {o.jmeno || `Uživatel #${o.uzivatelId}`}
                                </Typography>
                            ))}
                        </Grid>
                    </Grid>
                )}

                {/* Čas */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h3" color="text.secondary" fontWeight="bold">
                        <ClockFormatted variant="time" options={{ hour: "2-digit", minute: "2-digit", second: "2-digit" }} />
                    </Typography>
                </Box>
            </Box>

            <Box mt={2} mb={2} textAlign="center">
                <AppVersion />
            </Box>
        </Container>
    );
};

export default React.memo(PredpoplachDisplay);
